// A second Content-Security-Policy the host adds to every widget document, on top of the one the
// server baked in at upload. Browsers enforce every policy a document carries, so this one can only
// ever take capabilities away. It does two things:
//
// 1. Scripts: only the <script> elements actually in the reviewed document may run — each is
//    allowed by its SHA-256 hash. The server's policy has to say script-src 'unsafe-inline' (all
//    widget code is inline), which on its own would also let approved code fetch new code later and
//    run it by creating a <script>, or put it in an onclick="" attribute or a javascript: URL.
//    None of those match a hash, so none run. eval/new Function were already blocked.
// 2. Network: the server builds connect-src / img-src / form-action from every origin the code
//    uses. An installer can untick a network permission, and a newer upload's new origin starts out
//    unapproved, but neither changes that baked-in policy — so when the effective grants are known,
//    network access is limited to them here.

const NETWORK_PREFIX = 'network:';
// Same shape the server accepts for a network: permission (a bare https origin, optional port).
const ORIGIN_RE = /^https:\/\/[a-z0-9.-]+(:\d{1,5})?$/i;
const SERVER_CSP_RE = /<meta\s+http-equiv\s*=\s*["']Content-Security-Policy["'][^>]*>/i;
const DOCTYPE_RE = /^\s*<!doctype[^>]*>/i;

/** Origins from a manifest's `network:` permissions that are safe to put in a CSP. */
export function grantedNetworkOrigins(permissions: readonly string[] | null | undefined): string[] {
  const origins = new Set<string>();
  for (const p of permissions ?? []) {
    if (!p.toLowerCase().startsWith(NETWORK_PREFIX)) continue;
    const origin = p.slice(NETWORK_PREFIX.length);
    if (ORIGIN_RE.test(origin)) origins.add(origin.toLowerCase());
  }
  return Array.from(origins).sort();
}

async function sha256Base64(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  let binary = '';
  new Uint8Array(digest).forEach(b => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

/** CSP hash sources for every inline <script> in the document, as the browser will see them. */
export async function inlineScriptHashes(doc: string): Promise<string[]> {
  // DOMParser runs the same HTML parser the frame will, so each script's text (CRLF already
  // normalised) is exactly what the browser hashes when it checks script-src.
  const parsed = new DOMParser().parseFromString(doc, 'text/html');
  const hashes = new Set<string>();
  for (const script of Array.from(parsed.querySelectorAll('script'))) {
    if (script.hasAttribute('src')) continue; // the server never leaves one; CSP blocks it anyway
    hashes.add(`'sha256-${await sha256Base64(script.textContent ?? '')}'`);
  }
  return Array.from(hashes);
}

/**
 * The document with the host policy inserted before any widget code.
 * `networkOrigins` null = the effective grants aren't known here: leave network as the server set it.
 */
export async function withHostPolicy(doc: string, networkOrigins: readonly string[] | null): Promise<string> {
  const hashes = await inlineScriptHashes(doc);
  const directives = [`script-src ${hashes.length > 0 ? hashes.join(' ') : "'none'"}`];
  if (networkOrigins) {
    const sources = networkOrigins.length > 0 ? networkOrigins.join(' ') : "'none'";
    directives.push(
      `connect-src ${sources}`,
      `img-src data: blob:${networkOrigins.length > 0 ? ' ' + networkOrigins.join(' ') : ''}`,
      `form-action ${sources}`,
    );
  }
  const meta = `<meta http-equiv="Content-Security-Policy" content="${directives.join('; ')}">`;

  // Normally straight after the server's own CSP, which it injects ahead of everything else (and
  // before the SDK script). Only look near the top: a match deep inside the document would be
  // widget content, after scripts that already ran.
  const server = SERVER_CSP_RE.exec(doc.slice(0, 4096));
  if (server) {
    const at = server.index + server[0].length;
    return doc.slice(0, at) + meta + doc.slice(at);
  }
  // No server prelude (e.g. a pre-save local preview): after the doctype so the page doesn't
  // drop into quirks mode, otherwise first.
  const doctype = DOCTYPE_RE.exec(doc);
  if (doctype) {
    const at = doctype.index + doctype[0].length;
    return doc.slice(0, at) + meta + doc.slice(at);
  }
  return meta + doc;
}
